import React, { useState } from 'react';
import { Card } from '../components/ui/Card';
import { Lock, Mail, User, Eye, EyeOff, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function MyAccount() {
  const { user } = useAuth();
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Password strength checker
  const checkPasswordStrength = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };

    const score = Object.values(checks).filter(Boolean).length;
    const feedback = [];

    if (!checks.length) feedback.push('At least 8 characters');
    if (!checks.uppercase) feedback.push('One uppercase letter');
    if (!checks.lowercase) feedback.push('One lowercase letter');
    if (!checks.number) feedback.push('One number');
    if (!checks.special) feedback.push('One special character');

    return { score, feedback, checks };
  };

  // Get API URL
  const getApiUrl = () => {
    const isLocalhost = window.location.hostname === 'localhost';
    const isPlatformDomain = window.location.hostname === 'platform.glowytics.com';
    
    if (isLocalhost) {
      return 'http://localhost:5000';
    } else if (isPlatformDomain) {
      return 'https://api.glowytics.com';
    } else {
      return process.env.NODE_ENV === 'production' 
        ? 'https://api.glowytics.com' 
        : 'http://localhost:5000';
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure? This will permanently delete your account.')) {
      return;
    }
    setDeleteError('');
    setDeleteLoading(true);
    try {
      const apiUrl = getApiUrl();
      const resp = await fetch(`${apiUrl}/api/auth/delete-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirm: true })
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.error || 'Failed to delete account');
      }
      // Redirect to login after deletion
      localStorage.clear();
      window.location.href = '/login';
    } catch (e) {
      setDeleteError(e.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Update password strength when new password changes
  React.useEffect(() => {
    if (passwords.newPassword) {
      setPasswordStrength(checkPasswordStrength(passwords.newPassword));
    } else {
      setPasswordStrength({ score: 0, feedback: [] });
    }
  }, [passwords.newPassword]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Enhanced validation
    if (!passwords.currentPassword) {
      setError('Current password is required');
      setLoading(false);
      return;
    }

    if (!passwords.newPassword) {
      setError('New password is required');
      setLoading(false);
      return;
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordStrength.score < 4) {
      setError('Password does not meet security requirements');
      setLoading(false);
      return;
    }

    if (passwords.currentPassword === passwords.newPassword) {
      setError('New password must be different from current password');
      setLoading(false);
      return;
    }

    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/auth/update-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: passwords.currentPassword,
          newPassword: passwords.newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to update password');
      }

      setSuccess(data.message || 'Password updated successfully');
      setPasswords({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordStrength({ score: 0, feedback: [] });
    } catch (err) {
      console.error('Password update error:', err);
      setError(err.message || 'An error occurred while updating password');
    } finally {
      setLoading(false);
    }
  };

  // Tarih formatı için yardımcı fonksiyon
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Account Settings</h1>
        <div className="text-sm text-gray-500">
          Last updated: {formatDate(new Date())}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Profile Information */}
        <Card className="p-6 rounded-2xl">
          <h2 className="text-lg font-semibold mb-4">Profile Information</h2>
          <p className="text-sm text-gray-600 mb-6">
            Manage your account details and preferences.
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-6">
              <div className="w-24 h-24 bg-gray-100 rounded-2xl flex items-center justify-center">
                <User className="w-12 h-12 text-gray-400" />
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    <User className="w-4 h-4 inline mr-2" />
                    Full Name
                  </label>
                  <div className="text-gray-800">{user?.name || 'Not available'}</div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address
                  </label>
                  <div className="text-gray-800">{user?.email || 'Not available'}</div>
                </div>

              </div>
            </div>
          </div>
        </Card>

        {/* Security Settings */}
        <Card className="p-6 rounded-2xl">
          <h2 className="text-lg font-semibold mb-4">Security Settings</h2>
          <p className="text-sm text-gray-600 mb-6">
            Update your password and secure your account.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg">
              {success}
            </div>
          )}

          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Current Password</label>
              <div className="relative">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  className="w-full px-4 py-2 pr-20 rounded-full border bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter current password"
                  value={passwords.currentPassword}
                  onChange={(e) => setPasswords(prev => ({
                    ...prev,
                    currentPassword: e.target.value
                  }))}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <Lock className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">New Password</label>
              <div className="relative">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  className="w-full px-4 py-2 pr-20 rounded-full border bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter new password"
                  value={passwords.newPassword}
                  onChange={(e) => setPasswords(prev => ({
                    ...prev,
                    newPassword: e.target.value
                  }))}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <Lock className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>

              {/* Password Strength Indicator */}
              {passwords.newPassword && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Password Strength</span>
                    <span className={`text-xs font-medium ${
                      passwordStrength.score < 2 ? 'text-red-600' :
                      passwordStrength.score < 4 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {passwordStrength.score < 2 ? 'Weak' :
                       passwordStrength.score < 4 ? 'Medium' : 'Strong'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        passwordStrength.score < 2 ? 'bg-red-500' :
                        passwordStrength.score < 4 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                    ></div>
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <div className="text-xs text-gray-500">
                      <span>Missing: </span>
                      {passwordStrength.feedback.join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Confirm New Password</label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  className="w-full px-4 py-2 pr-20 rounded-full border bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Confirm new password"
                  value={passwords.confirmPassword}
                  onChange={(e) => setPasswords(prev => ({
                    ...prev,
                    confirmPassword: e.target.value
                  }))}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <Lock className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>

              {/* Password Match Indicator */}
              {passwords.confirmPassword && (
                <div className="mt-1">
                  {passwords.newPassword === passwords.confirmPassword ? (
                    <p className="text-xs text-green-600 flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Passwords match
                    </p>
                  ) : (
                    <p className="text-xs text-red-600 flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Passwords do not match
                    </p>
                  )}
                </div>
              )}
            </div>
            <button 
              type="submit"
              disabled={loading || passwordStrength.score < 4 || passwords.newPassword !== passwords.confirmPassword || !passwords.currentPassword}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Updating Password...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  Update Password
                </>
              )}
            </button>
          </form>
        </Card>

        {/* Danger Zone */}
        <Card className="p-6 rounded-2xl">
          <h2 className="text-lg font-semibold mb-4 text-red-700">Danger Zone</h2>
          <p className="text-sm text-gray-600 mb-4">Permanently delete your account and remove access to brands you’re shared on. If you own brands, transfer or delete them first.</p>
          {deleteError && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">{deleteError}</div>
          )}
          <button
            onClick={handleDeleteAccount}
            disabled={deleteLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {deleteLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Delete my account
              </>
            )}
          </button>
        </Card>

      </div>
    </div>
  );
} 